import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Button, Input, Spinner } from '@pijma/crypto'
import { useNumericMask } from '@pijma/input'
import { SourceInfo } from '@pijma/input/lib/dts/hooks/utils/types'

import { EPaymentMethod } from '@api/types'
import { Dispatch, RootState, store } from '@stores'
import { ECurrency, isoToECurrency, TPayFormState } from '@stores/types/*'
import {
  extractCurrencySymbol,
  localeNumber,
  removeLeadingZeroes,
} from '@utils'
import styled from 'styled-components'

import { EPaymentProvider } from '../../../../stores/types/EPaymentProvider'
import { TPaymentGateway } from '../../../../stores/types/TPaymentGatewayState'
import { H4 } from '../../../common/header/Headings'
import { InfoSearchData } from '../../../common/input/modalSelect/InfoSearchData'
import { ModalSelectInput } from '../../../common/input/modalSelect/ModalSelectInput'
import { Island } from '../../../common/island'
import { FlexBoxCenteredLayout } from '../../../common/layout/flex-box'
import NavigationBar from '../../../common/navigation/NavigationBar'
import { BodyText500, CaptionText500 } from '../../../common/text/BodyText'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 20px;
`

const InputContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px 24px;
`

const ModalSelectInputContainer = styled.div`
  width: 100%;
  padding: 0 20px 24px;
  position: relative;
`

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 8px 20px 20px;
`

const RateTipContainer = styled.div`
  color: #868686;
  padding: 0 32px 18px;
  text-align: center;
`

const SourceTitle = styled.div`
  display: flex;
  width: 100%;
  padding: 20px 24px 16px;
`

const NavigationBarWrapper = styled.div`
  padding: 0 4px;
  display: flex;
  width: 100%;
  align-items: start;
`

const TitleWrapper = styled.div`
  padding: 0 4px;
  display: flex;
  flex-direction: column;
  position: relative;
  top: -8px;
  margin-bottom: 16px;
  width: 100%;
  align-items: start;
`

const BackContainer = styled.div`
  margin: 24px 0 0 0;
  color: #217aac;

  span {
    cursor: pointer;

    :hover {
      color: rgb(49, 49, 49);
    }
  }
`

interface StateProps {
  payForm: TPayFormState
  prettyRate: number
}

interface DispatchProps {
  setAmount: (amountFrom: string, amountTo: string) => void
  setPaymentMethod: (method: string) => void
  setPaymentGateway: (paymentGateway?: TPaymentGateway) => void
}

type Props = StateProps & DispatchProps

const trueAmount = (formattedAmount: string) => {
  return formattedAmount.replace(',', '.').replaceAll(' ', '')
}

// Только сумму с запятой можно вставлять в поле для дальнейшего форматирования. Точка теряется.
// Костыль, но лучше не придумал, да и вроде как пофиг
const fixableAmount = (formattedAmount: string | undefined) => {
  if (formattedAmount == undefined) return ''
  return formattedAmount
    .replace('.', ',')
    .replaceAll(' ', '')
    .replace(/(\d+),(\d\d)\d*/g, '$1,$2')
}

const convertWithRate = (amount: string, rate: number) => {
  return fixableAmount(String(+amount * rate))
}

const convertWithReverseRate = (amount: string, rate: number) => {
  return convertWithRate(amount, 1 / rate)
}

const PurchaseSumPage = (props: Props) => {
  const intl = useIntl()
  const redirectTo = useNavigate()

  const [prettyAmountTo, setPrettyAmountTo] = useState('')
  const [prettyAmountFrom, setPrettyAmountFrom] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    const amount = +(props.payForm.amountFrom as string)
    const amountOutsideLimits =
      amount &&
      (amount < +(props.payForm.minLimit as string) ||
        amount > +(props.payForm.maxLimit as string))
    const incorrectAmount = Number.isNaN(amount)

    if (amountOutsideLimits) {
      setErrorMessage(
        `${intl.formatMessage({
          id: 'purchaseSumPage.amountOutsideLimits',
        })} ${localeNumber(props.payForm.minLimit)} ${intl.formatMessage({
          id: 'purchaseSumPage.to',
        })} ${localeNumber(props.payForm.maxLimit)} ${currencySign}`,
      )
    } else if (incorrectAmount) {
      setErrorMessage(
        intl.formatMessage({ id: 'purchaseSumPage.notLooksLikeAmount' }),
      )
    } else {
      setErrorMessage('')
    }
  }, [
    props.payForm.amountFrom,
    props.payForm.maxLimit,
    props.payForm.minLimit,
    props.payForm.application?.currency,
  ])

  const maskPropsMoneyTo = useNumericMask({
    value: prettyAmountTo,
    decimalScale: 2,
    allowedDecimalSeparators: ['%', '.', ',', 'б', 'ю'],
    decimalSeparator: ',',
    thousandsGroupStyle: 'thousand',
    thousandSeparator: '',
    staticSeparator: true,
    allowLeadingZeros: false,
    allowNegative: false,
    onValueChange: ({ formattedValue }, sourceInfo: SourceInfo) => {
      formattedValue = removeLeadingZeroes(formattedValue)
      if (sourceInfo.event) {
        const amountTo = trueAmount(formattedValue)
        const convertedAmountFrom = convertWithRate(amountTo, props.prettyRate)
        props.setAmount(trueAmount(convertedAmountFrom), amountTo)
        setPrettyAmountFrom(convertedAmountFrom)
      }
      setPrettyAmountTo(formattedValue)
    },
  })

  const maskPropsMoneyFrom = useNumericMask({
    value: prettyAmountFrom,
    decimalScale: 2,
    allowedDecimalSeparators: ['%', '.', ',', 'б', 'ю'],
    decimalSeparator: ',',
    thousandsGroupStyle: 'thousand',
    thousandSeparator: '',
    staticSeparator: true,
    allowLeadingZeros: false,
    allowNegative: false,
    onValueChange: ({ formattedValue }, sourceInfo: SourceInfo) => {
      formattedValue = removeLeadingZeroes(formattedValue)
      if (sourceInfo.event) {
        const amountFrom = trueAmount(formattedValue)
        const convertedAmountTo = convertWithReverseRate(
          amountFrom,
          props.prettyRate,
        )

        props.setAmount(amountFrom, trueAmount(convertedAmountTo))
        setPrettyAmountTo(convertedAmountTo)
      }
      setPrettyAmountFrom(formattedValue)
    },
  })

  const currencyFrom = isoToECurrency(
    props.payForm.paymentGateway?.currencyFrom,
  )

  const hideDataNav = currencyFrom === ECurrency.INR
  const dataNavLink = hideDataNav
    ? undefined
    : `../source/${props.payForm.paymentGateway?.paymentMethod}`

  const navigationBar = (
    <NavigationBarWrapper>
      <NavigationBar
        paymentMethodRedirectLink={'../source-select'}
        dataRedirectLink={dataNavLink}
        noData={hideDataNav}
        paymentDisabled
        noPaymentMethod={
          props.payForm.paymentGateway?.paymentMethod?.includes(
            EPaymentMethod.BANK_CARD,
          ) || props.payForm.paymentGateway?.provider === EPaymentProvider.SPAYZ
        }
      />
    </NavigationBarWrapper>
  )

  const visaCardSubtitle = (
    <TitleWrapper>
      <CaptionText500>
        {intl.formatMessage({ id: 'purchaseSumPage.visaSubtitle' })}
      </CaptionText500>
      <a
        href="/docs/visaCountries.pdf"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#337ab7', textDecoration: 'none' }}
      >
        <CaptionText500 color={'#217aac'}>
          {intl.formatMessage({ id: 'purchaseSumPage.visaAvailableCountries' })}
        </CaptionText500>
      </a>
    </TitleWrapper>
  )

  const subtitle =
    currencyFrom == ECurrency.USD ? visaCardSubtitle : navigationBar

  const currencySign = extractCurrencySymbol(
    props.payForm.paymentGateway?.currencyFrom,
  )

  function getCurrencyLabel() {
    return currencyFrom == ECurrency.UZS
      ? 'purchaseSumPage.uzs'
      : currencyFrom == ECurrency.USD
      ? 'purchaseSumPage.usd'
      : currencyFrom == ECurrency.INR
      ? 'purchaseSumPage.inr'
      : currencyFrom == ECurrency.THB
      ? 'purchaseSumPage.thb'
      : currencyFrom == ECurrency.VND
      ? 'purchaseSumPage.vnd'
      : 'purchaseSumPage.rubles'
  }

  const currencyLabel = getCurrencyLabel()

  const rateTip =
    currencyFrom == ECurrency.USD ? null : (
      <RateTipContainer>
        <CaptionText500>
          {intl.formatMessage({ id: 'purchaseSumPage.rateTip' })}
        </CaptionText500>
      </RateTipContainer>
    )

  const searchPaymentMethods: InfoSearchData[] =
    props.payForm.inrPaymentGateways?.map((gw) => ({
      id: gw.paymentGatewayId,
      name: gw.paymentMethod,
      logo:
        gw.paymentMethod === EPaymentMethod.UPI
          ? '/img/assets/upi.svg'
          : '/img/assets/imps.svg',
    })) ?? []

  const onPaymentMethodSelect = (methodId: string) => {
    const paymentMethod = searchPaymentMethods.find((it) => it.id === methodId)

    if (!paymentMethod) return

    props.setPaymentMethod(paymentMethod.name)

    const newPaymentGateway = props.payForm.inrPaymentGateways?.find(
      (gw) => gw.paymentGatewayId === methodId,
    )
    props.setPaymentGateway(newPaymentGateway)
  }

  const goBack = () => {
    currencyFrom == ECurrency.USD || currencyFrom == ECurrency.INR
      ? redirectTo(`../provider-select`)
      : redirectTo(`../source/${props.payForm.paymentGateway?.paymentMethod}`)
  }

  return (
    <FlexBoxCenteredLayout>
      <Container>
        {subtitle}
        <Island size="M" bgColor="primary">
          <SourceTitle data-testid="amount-page.header">
            <H4>{intl.formatMessage({ id: 'purchaseSumPage.title' })}</H4>
          </SourceTitle>
          <InputContainer data-testid="amount-page.input.usdt">
            <Input
              {...maskPropsMoneyTo}
              label={`USDT`}
              value={prettyAmountTo}
              width={'100%'}
              description={`1 USDT = ${localeNumber(
                props.prettyRate?.toString(),
              )} ${currencySign}`}
              disabled={props.payForm.paymentBlocking}
              type={'text'}
              inputMode={'decimal'}
              placeholder={'0'}
              allowClear={true}
            />
          </InputContainer>
          <InputContainer data-testid="amount-page.input.from">
            <Input
              {...maskPropsMoneyFrom}
              label={`${intl.formatMessage({ id: currencyLabel })}`}
              value={prettyAmountFrom}
              width={'100%'}
              errorMessage={errorMessage}
              // todo: обновлять лимиты по выбранному gw из выпадающего списка
              description={`${localeNumber(
                props.payForm.minLimit,
              )} – ${localeNumber(props.payForm.maxLimit)} ${currencySign}`}
              invalid={errorMessage != ''}
              disabled={props.payForm.paymentBlocking}
              type={'text'}
              inputMode={'decimal'}
              placeholder={'0'}
              allowClear={true}
            />
          </InputContainer>
          {props.payForm.paymentGateway?.provider == EPaymentProvider.SPAYZ &&
            currencyFrom === ECurrency.INR && (
              <ModalSelectInputContainer>
                <ModalSelectInput
                  defaultIcon={'/img/assets/upi.svg'}
                  data={searchPaymentMethods}
                  label={intl.formatMessage({
                    id: 'purchaseSumPage.paymentMethodLabel',
                  })}
                  defaultText={''}
                  searchTitle={intl.formatMessage({
                    id: 'purchaseSumPage.paymentMethodSearchTitle',
                  })}
                  isSearchInputEnabled={false}
                  searchPlaceholder={''}
                  onSelect={onPaymentMethodSelect}
                  selected={props.payForm.paymentGateway.paymentGatewayId}
                />
              </ModalSelectInputContainer>
            )}
          {rateTip}
          <ButtonContainer data-testid="amount-page.button.forward">
            <Button
              width={'100%'}
              size={'m'}
              disabled={
                props.payForm.paymentBlocking ||
                !+(props.payForm.amountFrom as string) ||
                errorMessage != ''
              }
              onPress={() => redirectTo('../find-offers')}
            >
              {props.payForm.paymentBlocking ? (
                <Spinner size={28} color={'#fff'} />
              ) : (
                intl.formatMessage({ id: 'purchaseSumPage.toPayment' })
              )}
            </Button>
          </ButtonContainer>
        </Island>
        <BackContainer
          onClick={() => goBack()}
          data-testid="payment-methods.back"
        >
          <BodyText500>
            {intl.formatMessage({ id: 'purchaseSourceSelectPage.back' })}
          </BodyText500>
        </BackContainer>
      </Container>
    </FlexBoxCenteredLayout>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  payForm: state.payForm,
  prettyRate: state.payForm?.rate as number,
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    setAmount: (amountFrom: string, amountTo: string) =>
      dispatch.payForm.set({
        amountFrom,
        amountTo,
      }),
    setPaymentMethod(method: string): void {
      const current = store.getState().payForm?.paymentGateway

      if (!current) return

      dispatch.payForm.set({
        paymentGateway: {
          ...current,
          paymentMethod: method as EPaymentMethod,
        },
      })
    },
    setPaymentGateway: (paymentGateway?: TPaymentGateway) => {
      dispatch.payForm.set({
        paymentGateway,
      })
    },
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PurchaseSumPage)
